import { AlertTriangle, CheckCircle2 } from 'lucide-react';

import { XoneraSpinner } from '@/components/core/common/feedback/XoneraSpinner';
// The auth shell stays in `features/auth/status/` - it is what all five
// non-form auth screens share, and account recovery only borrows it. Same
// reasoning as `DeactivatedAccountCard`.
import {
  AuthStatusButton,
  AuthStatusCard,
  AuthStatusLink,
} from '@/components/features/auth/status';

/**
 * What `/account/restore` can be showing.
 *
 * Lives here rather than in the page because the card is the thing that
 * branches on it; the page only decides which one is current. `idle` and
 * `restoring` are separate members but render identically - the page starts in
 * `idle` and moves to `restoring` the moment it has a token to redeem, and
 * there is no frame in between worth distinguishing to a user.
 */
export type RestoreState =
  | { status: 'idle' }
  | { status: 'restoring' }
  | { status: 'success'; email: string }
  /** `retryable` decides whether the sign-in-screen advice is appended. */
  | { status: 'error'; message: string; retryable: boolean };

/**
 * The four screens behind a recovery link, as one presentational component.
 *
 * Extracted out of `AccountRestorePage`, which used to compose these three
 * blocks inline. That is why every one of these states could only be storied
 * as a page story with an MSW override behind it: there was no component to
 * hand a state to. They are plain `args` now, and the page keeps the part that
 * is genuinely its own - reading the token, redeeming it once, and mapping a
 * failure through `resolveRestoreError`.
 *
 * The error branch is deliberately ONE block with a ternary rather than two.
 * `retryable` has exactly one visible consequence - whether the advice tail is
 * appended - and splitting the markup would invite the two to drift apart when
 * the only difference is a sentence.
 */
export function AccountRestoreCard({
  state,
  onGoToSignIn,
}: {
  state: RestoreState;
  onGoToSignIn: () => void;
}) {
  if (state.status === 'idle' || state.status === 'restoring') {
    return (
      <AuthStatusCard
        tone='info'
        icon={<XoneraSpinner className='h-5 w-5' label='' />}
        title='Restoring your account'
        body='One moment while we reactivate everything.'
      >
        <AuthStatusLink onClick={onGoToSignIn}>
          Cancel and go back
        </AuthStatusLink>
      </AuthStatusCard>
    );
  }

  if (state.status === 'success') {
    return (
      <AuthStatusCard
        tone='success'
        icon={<CheckCircle2 className='h-5 w-5' />}
        title='Your account is back'
        body={
          <>
            <span className='font-medium text-foreground'>{state.email}</span>{' '}
            is active again, with your lessons and progress as you left them.
          </>
        }
      >
        <AuthStatusButton onClick={onGoToSignIn}>Sign in</AuthStatusButton>
      </AuthStatusCard>
    );
  }

  return (
    <AuthStatusCard
      tone='error'
      icon={<AlertTriangle className='h-5 w-5' />}
      title='We could not restore this account'
      body={
        state.retryable
          ? `${state.message} If you are still inside the 30-day window, request a fresh link from the sign-in screen.`
          : state.message
      }
    >
      <AuthStatusButton variant='outline' onClick={onGoToSignIn}>
        Back to sign in
      </AuthStatusButton>
    </AuthStatusCard>
  );
}
